import * as React from 'react';
import { api } from '@/lib/utils';
import { useCurrentSystem } from '@/hooks/useCurrentSystem';
import { Button } from '@/components/ui/button';
import { PageWrapper } from '@/layout/components/PageWrapper.tsx';
import { PageHeader } from '@/layout/components/PageHeader.tsx';

interface FeatureRequest {
  id: number;
  title: string;
  description?: string;
  more_info_url?: string;
  status: number;
  total_votes: number;
  user_votes: number;
}

const STATUS_LABELS: Record<number, { label: string; color: string }> = {
  2: { label: 'בבדיקה', color: 'bg-orange-100 text-orange-900' },
  3: { label: 'בפיתוח', color: 'bg-blue-100 text-blue-900' },
  4: { label: 'הושלם', color: 'bg-green-100 text-green-900' },
  5: { label: 'נעול זמנית', color: 'bg-red-100 text-red-900' },
  6: { label: 'נדחה', color: 'bg-gray-100 text-gray-900' },
  1: { label: 'חדש', color: 'bg-gray-50 text-gray-900' },
};

export default function FeatureRequestPage() {
  const [features, setFeatures] = React.useState<FeatureRequest[]>([]);
  const [completed, setCompleted] = React.useState<FeatureRequest[]>([]);
  const [voteLock, setVoteLock] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState<Record<number, boolean>>({});
  // votes status for current school
  const [maxVotes, setMaxVotes] = React.useState<number | null>(null);
  const [activeVotes, setActiveVotes] = React.useState<number | null>(null);
  const { plan } = useCurrentSystem();

  // when true, user cannot add more votes (either plan has 0 votes or already used all)
  const cannotAddVote = React.useMemo(() => {
    if (maxVotes === null || activeVotes === null) return false; // still loading
    if (maxVotes === 0) return true; // no subscription for voting
    return activeVotes >= maxVotes;
  }, [maxVotes, activeVotes]);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [fRes, cRes] = await Promise.all([
        api.get('feature-request/list/'),
        api.get('feature-request/completed/'),
      ]);
      setFeatures(fRes.data);
      setCompleted(cRes.data);
      // refresh vote status after loading lists
      await fetchStatus();
    } catch (e) {
      setError('שגיאה בטעינת הנתונים');
    }
  };

  // fetch the school's vote counts from backend
  const fetchStatus = async () => {
    try {
      const res = await api.post('feature-request/status/');
      setMaxVotes(res.data.max_votes ?? 0);
      setActiveVotes(res.data.active_votes ?? 0);
    } catch (e) {
      // don't override main error state; log for debugging
      // console.warn('failed to fetch vote status', e);
    }
  };

  const handleVote = async (featureId: number, action: 'add' | 'remove') => {
    setVoteLock(true);
    setError(null);
    try {
      await api.post('feature-request/vote/', { feature_id: featureId, action });
      // refresh lists and vote status after successful vote
      await fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'שגיאה בביצוע הפעולה');
      // try to refresh status even on error to reflect server state
      try { await fetchStatus(); } catch (_) {}
    } finally {
      setVoteLock(false);
    }
  };

  return (
    <PageWrapper>
      <PageHeader title="הצבעה על תכונות חדשות" />

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded shadow px-6 py-5">
          <div className="text-base text-neutral-700 mb-2">
            כאן תוכלו להצביע עבור תכונות שחשובות לכם. כל מוסד מקבל כמות הצבעות לפי המסלול. ניתן להצביע עבור תכונה אחת מספר פעמים. לאחר שתכונה מושלמת, ניתן להצביע מחדש עבור תכונות אחרות.
          </div>
          <div className="text-base text-neutral-700 mb-4">
            יש לכם רעיון לפיתוח חדש? אנחנו פתוחים לשמוע! פתחו קריאת שירות חדשה <a href="/crm/servicecall/" className="text-blue-600 underline">כאן</a>.
          </div>

          <div className="text-sm mb-4 text-neutral-600">
            {maxVotes === null ? (
              <span>טוען סטטוס הצבעות...</span>
            ) : maxVotes === 0 ? (
              <span>למוסד זה אין מנוי להצבעות</span>
            ) : (
              <span>
                הינכם נמצאים ב{plan?.name ?? '---'}, ניצלתם {activeVotes ?? 0} הצבעות, מתוך {maxVotes ?? 0} הצבעות שברשותכם.
              </span>
            )}
          </div>

          {error && <div className="mb-4 text-red-600">{error}</div>}

          <div className="flex gap-6">
             {/* טבלת תכונות עתידיות */}
            <div className="w-2/3">
              <h2 className="text-lg font-semibold mb-2 text-neutral-800">הצבעה על תכונות עתידיות</h2>
              <div className="overflow-x-auto rounded">
                <table className="min-w-full text-right bg-white">
                   <thead>
                     <tr className="bg-gray-100">
                       <th className="p-2">מזהה</th>
                       <th className="p-2">הצעה/בקשה</th>
                       <th className="p-2">כמות הצבעות</th>
                       <th className="p-2">ההצבעות שלך</th>
                       <th className="p-2">סטטוס</th>
                     </tr>
                   </thead>
                   <tbody>
                     {features.map((f) => (
                       <tr key={f.id} className="border-b last:border-b-0">
                         <td className="p-2 font-mono">{f.id}</td>
                         <td className="p-2">
                          <div className="flex flex-col gap-1">
                            <span className="text-neutral-800">{f.title}</span>
                            {f.description && (
                              <span className="text-xs text-neutral-500 flex items-center cursor-pointer" onClick={() => setExpanded(e => ({...e, [f.id]: !e[f.id]}))}>
                                <span className="mr-1">{expanded[f.id] ? 'הסתר' : 'הצג עוד'}</span>
                                <span className={`transition-transform ${expanded[f.id] ? 'rotate-90' : ''}`}>▶</span>
                              </span>
                            )}
                            {expanded[f.id] && f.description && (
                              <span className="text-xs text-neutral-700 bg-gray-50 rounded p-1 mt-1">{f.description}</span>
                            )}
                            {typeof f.more_info_url === 'string' && f.more_info_url.length > 0 && (
                              <a href={f.more_info_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline">לפרטים נוספים</a>
                            )}
                          </div>
                         </td>
                        <td className="p-2 text-center">{f.total_votes}</td>
                        <td className="p-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={voteLock || f.status === 5 || f.status === 4 || cannotAddVote}
                              className={`${(voteLock || f.status === 5 || f.status === 4 || cannotAddVote) ? 'opacity-50 cursor-not-allowed' : ''}`}
                              aria-disabled={voteLock || f.status === 5 || f.status === 4 || cannotAddVote}
                              title={cannotAddVote ? 'נוצלו כל ההצבעות הזמינות' : undefined}
                              onClick={() => handleVote(f.id, 'add')}
                            >▲</Button>
                            <span className="text-neutral-800">{f.user_votes}</span>
                            <Button size="sm" variant="ghost" disabled={voteLock || f.user_votes === 0} onClick={() => handleVote(f.id, 'remove')}>▼</Button>
                          </div>
                        </td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${STATUS_LABELS[f.status]?.color || ''}`}>{STATUS_LABELS[f.status]?.label || ''}</span>
                        </td>
                       </tr>
                     ))}
                     {features.length === 0 && (
                      <tr><td colSpan={5} className="p-4 text-center text-neutral-400">אין תכונות זמינות להצבעה</td></tr>
                     )}
                   </tbody>
                 </table>
               </div>
            </div>
            {/* טבלת תכונות שבוצעו */}
            <div className="w-1/3">
              <h2 className="text-lg font-semibold mb-2 text-neutral-800">רשימת תכונות שבוצעו</h2>
              <div className="overflow-x-auto rounded">
                <table className="min-w-full text-right bg-green-50">
                   <thead>
                     <tr className="bg-green-100">
                       <th className="p-2">מזהה</th>
                       <th className="p-2">שם</th>
                       <th className="p-2">כמות הצבעות</th>
                     </tr>
                   </thead>
                   <tbody>
                     {completed.map((f) => (
                       <tr key={f.id} className="border-b last:border-b-0">
                         <td className="p-2 font-mono">{f.id}</td>
                         <td className="p-2">{f.title}</td>
                         <td className="p-2 text-center">{f.total_votes}</td>
                       </tr>
                     ))}
                     {completed.length === 0 && (
                      <tr><td colSpan={3} className="p-4 text-center text-neutral-400">אין תכונות שבוצעו</td></tr>
                     )}
                   </tbody>
                 </table>
               </div>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
   );
 }
